import {
    coreExtensionData,
    createExtensionInput,
    ExtensionDefinition,
} from "@backstage/frontend-plugin-api";
import {
    EntityCardBlueprint,
    EntityContentBlueprint,
} from "@backstage/plugin-catalog-react/alpha";

/**
 * Factory for relationship content tabs.
 *
 * Each tab exposes a `cards` extension input so cards can be attached (or
 * removed) purely through `app.extensions` config - no code change needed.
 * All tabs are scoped to kind:Component.
 *
 * The loader uses a dynamic import of RelationshipCardsContent.tsx for code
 * splitting -- the component is excluded from the main bundle until the tab
 * is first opened.
 */
function makeRelationshipsContent(
    name: string,
    title: string,
    path: string
): ExtensionDefinition {
    return EntityContentBlueprint.makeWithOverrides({
        name,
        inputs: {
            cards: createExtensionInput([
                coreExtensionData.reactElement,
                EntityContentBlueprint.dataRefs.filterFunction.optional(),
                EntityCardBlueprint.dataRefs.type.optional(),
            ]),
        },
        factory: (originalFactory, { node, inputs }) =>
            originalFactory({
                path,
                title,
                filter: "kind:component",
                loader: async () => {
                    const { RelationshipCardsContent } = await import(
                        "./RelationshipCardsContent"
                    );
                    return (
                        <RelationshipCardsContent
                            node={node}
                            cards={inputs.cards}
                        />
                    );
                },
            }),
    });
}

/**
 * APIs relationship tab — provided, consumed, and all APIs for this component.
 * Cards attach via: attachTo: { id: entity-content:catalog/relationships-apis, input: cards }
 */
export const apisContent = makeRelationshipsContent(
    "relationships-apis",
    "APIs",
    "/relationship-apis"
);

/**
 * Subcomponents tab — subcomponents of this component.
 * Cards attach via: attachTo: { id: entity-content:catalog/relationships-subcomponents, input: cards }
 */
export const subcomponentsContent = makeRelationshipsContent(
    "relationships-subcomponents",
    "Subcomponents",
    "/relationship-subcomponents"
);

/**
 * Dependencies tab — components and resources this component depends on.
 * Cards attach via: attachTo: { id: entity-content:catalog/relationships-dependencies, input: cards }
 */
export const dependenciesContent = makeRelationshipsContent(
    "relationships-dependencies",
    "Dependencies",
    "/relationship-dependencies"
);
